import { useEffect, useRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Target,
  Trophy,
  Flame,
  TrendingUp,
  MessageCircle,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  toast,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@cliently/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router";
import { api } from "../lib/api";
import { createPortalConversation } from "../lib/chatApi";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { LottieIllustration } from "../components/LottieIllustration";
import emptyBoxLottie from "../assets/lottie/empty-box.json";

interface MilestoneRow {
  percent: number;
  emoji: string;
  label: string;
}

interface ObjectiveDayRow {
  id: string;
  date: string; // YYYY-MM-DD
  progress: number;
  celebratedProgress?: number;
  doneAt: string | null;
}

interface ObjectiveRow {
  id: string;
  title: string;
  description: string | null;
  progress: number;
  clientProgressEditable: boolean;
  icon: string | null;
  milestones: MilestoneRow[] | null;
  target?:
    | { type: "NUMBER"; value: number; unit?: string }
    | { type: "DURATION"; value: number; unit?: "seconds" }
    | { type: "BOOLEAN"; value: boolean; unit?: string }
    | { type: "AMOUNT"; value: number; unit?: string }
    | { type: "FREQUENCY"; value: number; unit?: string }
    | null;
  doneAt: string | null;
  days: ObjectiveDayRow[];
  client?: {
    org?: { id: string; name: string } | null;
  };
}



const formatObjectiveTarget = (target: ObjectiveRow["target"], t: TFunction): string | null => {
  if (!target) return null;
  if (target.type === "BOOLEAN") {
    return target.value ? t("common.yes", "Oui") : t("common.no", "Non");
  }
  if (target.type === "DURATION") {
    const totalSeconds = Math.max(0, Math.round(target.value));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes} min ${seconds}s`;
  }
  if (target.type === "AMOUNT")
    return `${target.value} ${target.unit ?? "EUR"}`;
  if (target.type === "FREQUENCY") {
    return `${target.value} ${target.unit ?? t("objectivesPage.perWeek", "par semaine")}`;
  }
  return `${target.value} ${target.unit ?? t("objectivesPage.units", "unités")}`;
}



const usePortalObjectives = (activeClientId: string | null) => {
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;

  return useQuery({
    queryKey: ["portal-objectives-page", activeClientId],
    queryFn: async () => {
      const { data } = await api.get<ObjectiveRow[]>(
        isAllProsMode ? "/portal/aggregate/objectives" : "/portal/objectives",
      );
      return data;
    },
    // Rafraîchit la page pendant qu'elle est ouverte pour refléter
    // rapidement les modifications faites côté pro.
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });
}



const getProgressColor = (progress: number): string => {
  if (progress >= 100) return "bg-green-500";
  if (progress >= 50) return "bg-primary";
  if (progress >= 25) return "bg-primary/70";
  return "bg-primary/40";
}



const fireConfetti = () => {
  const duration = 2000;
  const end = Date.now() + duration;
  const frame = () => {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
}



const useMilestoneConfetti = (objectives: ObjectiveRow[], canPersist: boolean) => {
  const hasChecked = useRef(false);

  const checkAndFire = useCallback(() => {
    if (hasChecked.current || objectives.length === 0) return;
    hasChecked.current = true;

    let newlyUnlocked: {
      emoji: string;
      label: string;
      dayId: string;
      celebratedProgress: number;
    } | null = null;

    for (const obj of objectives) {
      const milestones = Array.isArray(obj.milestones) ? obj.milestones : [];
      if (milestones.length === 0) continue;
      const sorted = [...milestones].sort((a, b) => a.percent - b.percent);

      for (const day of obj.days ?? []) {
        const current = day.progress ?? 0;
        const stored = day.celebratedProgress ?? 0;

        for (const m of sorted) {
          if (current >= m.percent && stored < m.percent) {
            newlyUnlocked = {
              emoji: m.emoji,
              label: m.label,
              dayId: day.id,
              celebratedProgress: m.percent,
            };
          }
        }
      }
    }

    if (newlyUnlocked && canPersist) {
      void api.patch(
        `/portal/objective-days/${newlyUnlocked.dayId}/celebration-progress`,
        {
          celebratedProgress: newlyUnlocked.celebratedProgress,
        },
      );
      fireConfetti();
      showToast(newlyUnlocked.emoji, newlyUnlocked.label);
    }
  }, [canPersist, objectives]);

  useEffect(() => {
    checkAndFire();
  }, [checkAndFire]);
}



const showToast = (emoji: string, label: string) => {
  const el = document.createElement("div");
  el.className =
    "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl bg-white px-5 py-3 shadow-lg border animate-in slide-in-from-bottom-4 fade-in duration-500";
  el.innerHTML = `
    <span class="text-2xl">${emoji}</span>
    <div>
      <p class="text-sm font-semibold text-gray-900">Félicitations !</p>
      <p class="text-xs text-gray-500">Vous avez débloqué le palier ${emoji} ${label} !</p>
    </div>
  `;
  document.body.appendChild(el);
  setTimeout(() => {
    el.classList.add("opacity-0", "transition-opacity", "duration-500");
    setTimeout(() => el.remove(), 500);
  }, 4000);
}



export const ObjectivesPage = () => {
  const { t } = useTranslation();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: objectives = [], isLoading } =
    usePortalObjectives(activeClientId);

  useMilestoneConfetti(objectives, !isAllProsMode);

  const dayCards = (objectives ?? []).flatMap((obj) =>
    (obj.days ?? []).map((day) => ({
      objective: obj,
      day,
    })),
  );

  // Dédoublonner les cartes actives par objectif : on ne garde que le jour
  // le plus récent (en privilégiant « aujourd'hui » s'il existe) pour éviter
  // qu'un même objectif apparaisse plusieurs fois dans « En cours ».
  const activeByObjective = new Map<string, (typeof dayCards)[number]>();
  for (const card of dayCards) {
    const { objective, day } = card;
    const isActive =
      objective.doneAt === null && day.doneAt === null && day.progress < 100;
    if (!isActive) continue;

    const existing = activeByObjective.get(objective.id);
    if (!existing) {
      activeByObjective.set(objective.id, card);
      continue;
    }

    if (
      day.date > existing.day.date ||
      (day.date === existing.day.date && day.progress > existing.day.progress)
    ) {
      activeByObjective.set(objective.id, card);
    }
  }
  const activeDayCards = Array.from(activeByObjective.values());

  // Côté portail client, on veut éviter les doublons de trophées/terminés
  // lorsqu'un même objectif possède plusieurs lignes jour complétées.
  // On regroupe donc les jours complétés par `objective.id` et on ne garde
  // qu'une seule carte par objectif (le jour le plus récent).
  const completedByObjective = new Map<string, (typeof dayCards)[number]>();
  for (const card of dayCards) {
    const { objective, day } = card;
    const isCompleted =
      objective.doneAt !== null || day.doneAt !== null || day.progress >= 100;
    if (!isCompleted) continue;

    const existing = completedByObjective.get(objective.id);
    if (!existing) {
      completedByObjective.set(objective.id, card);
      continue;
    }

    const existingDay = existing.day;
    // On privilégie le jour le plus récent ; si la date est identique,
    // on prend celui avec le pourcentage de progression le plus élevé.
    if (
      day.date > existingDay.date ||
      (day.date === existingDay.date && day.progress > existingDay.progress)
    ) {
      completedByObjective.set(objective.id, card);
    }
  }
  const completedDayCards = Array.from(completedByObjective.values());

  if (isLoading) {
    return (
      <div className="w-full space-y-6">
        <div className="bg-muted h-24 animate-pulse rounded-xl" />
        <div className="grid w-full gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="bg-muted h-40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const activeCount = activeDayCards.length;
  const completedCount = completedDayCards.length;
  const bothSectionsEmpty = activeCount === 0 && completedCount === 0;

  return (
    <div className="w-full space-y-8">
      <div className="space-y-5">
        <div className="rounded-2xl border border-border/50 bg-linear-to-br from-background via-background to-muted/25 p-4 sm:p-5">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("objectivesPage.title", "Mes objectifs")}
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
            {t(
              "objectivesPage.subtitle",
              "Suivez vos objectifs avec votre praticien, actifs et terminés.",
            )}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-border/60 bg-linear-to-br from-orange-500/8 via-card to-card shadow-none">
            <CardContent className="flex items-center justify-between p-4">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">
                  {t("objectivesPage.activeSection", "En cours")}
                </p>
                <p className="text-2xl font-semibold tabular-nums">{activeCount}</p>
              </div>
              <div className="rounded-xl border border-orange-200/50 bg-orange-500/10 p-2.5 text-orange-600 dark:border-orange-900/40">
                <Flame className="h-4 w-4" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60 bg-linear-to-br from-emerald-500/8 via-card to-card shadow-none">
            <CardContent className="flex items-center justify-between p-4">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">
                  {isAllProsMode
                    ? t("objectivesPage.trophiesSection", "Trophées")
                    : t("objectivesPage.completedSection", "Terminés")}
                </p>
                <p className="text-2xl font-semibold tabular-nums">{completedCount}</p>
              </div>
              <div className="rounded-xl border border-emerald-200/50 bg-emerald-500/10 p-2.5 text-emerald-600 dark:border-emerald-900/40">
                <CheckCircle2 className="h-4 w-4" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {bothSectionsEmpty ? (
        <Card className="border-border/40 border shadow-none">
          <CardContent className="text-muted-foreground flex flex-col items-center gap-3 py-10 text-center text-sm">
            <LottieIllustration
              animationData={emptyBoxLottie}
              size={200}
              className="opacity-90"
            />
            {t(
              isAllProsMode
                ? "objectivesPage.bothEmptyAllPros"
                : "objectivesPage.bothEmpty",
              isAllProsMode
                ? "Aucun objectif en cours ni trophée pour le moment."
                : "Aucun objectif en cours ni terminé pour le moment.",
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <section className="space-y-3 rounded-2xl border border-border/50 bg-card/40 p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-primary h-4 w-4" />
              <h2 className="font-semibold tracking-tight">
                {t("objectivesPage.activeSection", "En cours")}
              </h2>
            </div>
            {activeDayCards.length === 0 ? (
              <Card className="border-border/40 border shadow-none">
                <CardContent className="text-muted-foreground flex flex-col items-center gap-3 py-10 text-center text-sm">
                  <LottieIllustration
                    animationData={emptyBoxLottie}
                    size={200}
                    className="opacity-90"
                  />
                  {t("objectivesPage.noActive", "Aucun objectif en cours.")}
                </CardContent>
              </Card>
            ) : (
              <div
                className={cn(
                  "grid w-full gap-4",
                  activeDayCards.length > 1 && "md:grid-cols-2",
                  activeDayCards.length > 2 && "2xl:grid-cols-3",
                )}
              >
                {activeDayCards.map(({ objective, day }) => (
                  <ObjectiveCard
                    key={day.id}
                    objective={objective}
                    day={day}
                    isAllProsMode={isAllProsMode}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3 rounded-2xl border border-border/50 bg-card/40 p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              <h2 className="font-semibold tracking-tight">
                {isAllProsMode
                  ? t("objectivesPage.trophiesSection", "Trophées")
                  : t("objectivesPage.completedSection", "Terminés")}
              </h2>
            </div>
            {completedDayCards.length === 0 ? (
              <Card className="border-border/40 border shadow-none">
                <CardContent className="text-muted-foreground flex flex-col items-center gap-3 py-10 text-center text-sm">
                  <LottieIllustration
                    animationData={emptyBoxLottie}
                    size={180}
                    className="opacity-90"
                  />
                  {t(
                    "objectivesPage.noCompleted",
                    isAllProsMode
                      ? "Aucun trophée pour le moment."
                      : "Aucun objectif terminé pour le moment.",
                  )}
                </CardContent>
              </Card>
            ) : (
              <div
                className={cn(
                  "grid w-full gap-4",
                  completedDayCards.length > 1 && "md:grid-cols-2",
                  completedDayCards.length > 2 && "2xl:grid-cols-3",
                )}
              >
                {completedDayCards.map(({ objective, day }) => (
                  <ObjectiveCard
                    key={day.id}
                    objective={objective}
                    day={day}
                    isAllProsMode={isAllProsMode}
                    completed
                    showClientOrg={false}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}



const getAccentBorder = (progress: number, completed: boolean): string => {
  return "border-l-border/70";
}



const ObjectiveCard = ({
  objective,
  day,
  isAllProsMode,
  completed = false,
  showClientOrg = true,
}: {
  objective: ObjectiveRow;
  day: ObjectiveDayRow;
  isAllProsMode: boolean;
  completed?: boolean;
  showClientOrg?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const todayKey = new Date().toISOString().slice(0, 10);

  const [creatingChat, setCreatingChat] = useState(false);
  const [askChatDialogOpen, setAskChatDialogOpen] = useState(false);

  const [currentProgress, setCurrentProgress] = useState<number>(day.progress);
  useEffect(() => {
    setCurrentProgress(day.progress);
  }, [day.progress]);

  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        globalThis.clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const commitProgressUpdate = async (nextProgress: number) => {
    setIsUpdatingProgress(true);
    try {
      await api.patch(`/portal/objective-days/${day.id}/progress`, {
        progress: nextProgress,
      });
      queryClient.invalidateQueries({
        queryKey: ["portal-objectives-page", activeClientId],
      });
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  const scheduleProgressUpdate = (nextProgress: number) => {
    if (!objective.clientProgressEditable) return;
    if (day.date > todayKey) return;
    if (updateTimeoutRef.current) {
      globalThis.clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = globalThis.setTimeout(() => {
      commitProgressUpdate(nextProgress);
      updateTimeoutRef.current = null;
    }, 250);
  };
  const doneDate = day.doneAt
    ? new Date(day.doneAt).toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const milestones = Array.isArray(objective.milestones)
    ? objective.milestones
    : [];
  const sorted = [...milestones].sort((a, b) => a.percent - b.percent);
  const isActuallyCompleted =
    completed || day.doneAt !== null || currentProgress >= 100;
  const targetLabel = formatObjectiveTarget(objective.target, t);

  const openChatWithPractitioner = async () => {
    if (creatingChat) return;
    setCreatingChat(true);
    try {
      const subjectBase = t(
        "objectivesPage.chatSubject",
        "Question — objectif",
      );
      const subject = `${subjectBase}: ${objective.title}`.slice(0, 120);
      const initialMessage = t(
        "objectivesPage.chatBodyObjective",
        "Bonjour,\n\nJe vous contacte au sujet de l’objectif « {{title}} » (jour du {{date}}).\n\n",
        { title: objective.title, date: day.date },
      );
      const conv = await createPortalConversation(
        subject,
        objective.client?.org?.id,
        initialMessage,
      );
      setAskChatDialogOpen(false);
      navigate(`/chat/${conv.id}`);
    } catch {
      toast.error(
        t(
          "objectivesPage.chatError",
          "Impossible d’ouvrir la conversation.",
        ),
      );
    } finally {
      setCreatingChat(false);
    }
  };

  return (
    <>
    <Card
      className={cn(
        "border-l-4 border border-border/60 bg-linear-to-br from-card via-card to-muted/20 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md",
        getAccentBorder(currentProgress, isActuallyCompleted),
        isActuallyCompleted ? "bg-card/80" : "bg-card",
      )}
    >
      <CardHeader className="min-h-20 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {objective.icon ? (
              <div className="bg-primary/5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xl">
                {objective.icon}
              </div>
            ) : (
              <div
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                  isActuallyCompleted
                    ? "bg-emerald-50 dark:bg-emerald-950/30"
                    : "bg-primary/5",
                )}
              >
                <Target
                  className={cn(
                    "h-5 w-5",
                    isActuallyCompleted
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-primary",
                  )}
                />
              </div>
            )}
            <div className="min-w-0 space-y-1">
              <CardTitle
                className={cn(
                  "text-[15px] leading-snug",
                  isActuallyCompleted &&
                    "text-muted-foreground decoration-muted-foreground/60 line-through",
                )}
              >
                {objective.title}
              </CardTitle>
              {showClientOrg && isAllProsMode && objective.client?.org?.name ? (
                <p className="text-muted-foreground text-xs font-medium">
                  {objective.client.org.name}
                </p>
              ) : null}
              {targetLabel ? (
                <p className="text-muted-foreground text-[11px]">
                  {t("objectivesPage.target", "Cible")} : {targetLabel}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex min-w-[68px] shrink-0 flex-col items-end gap-1 text-right">
            {!isActuallyCompleted ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 text-xs shadow-none"
                disabled={creatingChat}
                onClick={(e) => {
                  e.stopPropagation();
                  setAskChatDialogOpen(true);
                }}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("objectivesPage.askCoach", "Poser une question")}
                </span>
                <span className="sm:hidden">Question</span>
              </Button>
            ) : null}
            <span
              className={cn(
                "tabular-nums tracking-tight leading-none",
                isActuallyCompleted
                  ? "text-lg font-semibold text-emerald-600 dark:text-emerald-400"
                  : currentProgress >= 50
                    ? "text-primary text-2xl font-bold"
                    : "text-primary/75 text-2xl font-semibold",
              )}
              aria-label={t(
                "objectivesPage.progressPercentAria",
                "{{value}} % d'avancement",
                {
                  value: currentProgress,
                },
              )}
            >
              {currentProgress}%
            </span>
            {isActuallyCompleted && doneDate ? (
              <Badge
                variant="secondary"
                className="border-emerald-200/90 bg-emerald-50 text-[11px] font-medium text-emerald-950 dark:border-emerald-800/80 dark:bg-emerald-950/50 dark:text-emerald-100"
              >
                {t("objectivesPage.completedOn", "Terminé le {{date}}", {
                  date: doneDate,
                })}
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      {!isActuallyCompleted && (
        <CardContent className="space-y-2.5 pt-0.5">
          <div className="relative">
            <div className="bg-muted h-2.5 w-full overflow-hidden rounded-full">
              <div
                className={cn(
                  "h-2.5 rounded-full transition-all duration-500",
                  getProgressColor(currentProgress),
                )}
                style={{ width: `${Math.min(100, currentProgress)}%` }}
              />
            </div>
            <div
              className="pointer-events-none absolute inset-y-0 left-0 rounded-full bg-white/20"
              style={{ width: `${Math.min(100, currentProgress)}%` }}
            />
            {sorted.map((m) => (
              <div
                key={`${m.percent}-${m.emoji}`}
                className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${m.percent}%` }}
              >
                <div className="flex h-5 w-5 items-center justify-center">
                  <div
                    className={cn(
                      "rounded-full border-2 border-white shadow-sm transition-all",
                      currentProgress >= m.percent
                        ? "h-2.5 w-2.5 bg-primary"
                        : "bg-muted-foreground/25 h-2 w-2",
                    )}
                  />
                </div>
              </div>
            ))}

            {objective.clientProgressEditable && day.date <= todayKey && (
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={currentProgress}
                disabled={isUpdatingProgress}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (!Number.isInteger(v)) return;
                  setCurrentProgress(v);
                  scheduleProgressUpdate(v);
                }}
                className="absolute inset-0 h-2.5 w-full cursor-pointer opacity-0"
              />
            )}
          </div>

          {sorted.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto px-0.5 pb-0.5">
              {sorted.map((m) => {
                const unlocked = currentProgress >= m.percent;
                return (
                  <div
                    key={`${m.percent}-${m.label}`}
                    className={cn(
                      "min-w-[64px] shrink-0 flex flex-col items-center gap-0.5 rounded-md border px-1.5 py-1 transition-all",
                      unlocked ? "border-primary/35 dark:border-primary/40" : "border-border/60",
                      unlocked && "bg-primary/5 dark:bg-primary/10",
                    )}
                  >
                    <span
                      className={cn(
                        "text-lg transition-all duration-300",
                        unlocked
                          ? "scale-110 grayscale-0"
                          : "scale-90 opacity-30 grayscale",
                      )}
                    >
                      {m.emoji}
                    </span>
                    <span
                      className={cn(
                        "max-w-[72px] truncate text-center text-[9px] leading-tight",
                        unlocked
                          ? "text-foreground font-medium"
                          : "text-muted-foreground",
                      )}
                      title={m.label}
                    >
                      {m.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {objective.description ? (
            <p className="text-foreground/90 bg-background mt-1 rounded-md border border-border/80 p-2 whitespace-pre-wrap text-xs leading-relaxed shadow-xs">
              {objective.description}
            </p>
          ) : null}
        </CardContent>
      )}
      {isActuallyCompleted && objective.description ? (
        <CardContent className="border-border/50 border-t pt-3">
          <p className="text-muted-foreground whitespace-pre-wrap text-sm leading-relaxed">
            {objective.description}
          </p>
        </CardContent>
      ) : null}
    </Card>

    <Dialog
      open={askChatDialogOpen}
      onOpenChange={(open) => {
        if (!open && creatingChat) return;
        setAskChatDialogOpen(open);
      }}
    >
      <DialogContent className="sm:max-w-md sm:rounded-xl">
        <DialogHeader className="space-y-2">
          <DialogTitle>
            {t(
              "objectivesPage.askConfirmTitle",
              "Contacter votre praticien ?",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              "objectivesPage.askConfirmDescription",
              "Une conversation sera ouverte dans Messages au sujet de l’objectif « {{title}} ».",
              { title: objective.title },
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            disabled={creatingChat}
            onClick={() => setAskChatDialogOpen(false)}
          >
            {t("common.cancel", "Annuler")}
          </Button>
          <Button
            type="button"
            disabled={creatingChat}
            onClick={() => void openChatWithPractitioner()}
          >
            {creatingChat ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t("objectivesPage.askConfirmContinue", "Continuer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
